import type { InstallPhase } from 'src/api/queries/getInstallHealth';

/**
 * Phase classification shared by the install banner and the setup page.
 * The banner-specific copy/state mapping and the setup-page copy live with
 * their own components — this module is only the phase taxonomy.
 */

/** Phases where the pipeline is actively loading data. */
export const ACTIVE_INSTALL_PHASES: readonly InstallPhase[] = [
  'CONNECTING',
  'CONNECTED',
  'LOADING_RECENT',
  'RECENT_READY',
  'LOADING_FULL',
];

/** Recoverable end states — install settled in a reduced mode. */
export const TERMINAL_INSTALL_PHASES: readonly InstallPhase[] = ['DEGRADED', 'BROWSE_ONLY'];

export function isActiveInstallPhase(phase: InstallPhase | null): phase is InstallPhase {
  return phase != null && ACTIVE_INSTALL_PHASES.includes(phase);
}

export function isTerminalInstallPhase(phase: InstallPhase | null): phase is InstallPhase {
  return phase != null && TERMINAL_INSTALL_PHASES.includes(phase);
}

export function isInstallComplete(phase: InstallPhase | null): boolean {
  return phase === 'FULL_READY';
}

/** Hours elapsed since an ISO timestamp; null if absent or unparseable. */
export function hoursSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return (Date.now() - ms) / 3_600_000;
}

/** True when `iso` is in the past and less than `hours` ago. */
export function withinHours(iso: string | null | undefined, hours: number): boolean {
  const elapsed = hoursSince(iso);
  return elapsed != null && elapsed >= 0 && elapsed < hours;
}

/**
 * The four merchant-facing milestones on the setup page timeline. The eight
 * internal phases collapse onto these — a merchant reasons about "full
 * history is loading", not `LOADING_FULL`.
 */
export const INSTALL_MILESTONES = [
  'Connect store',
  'Recent orders',
  'Full history',
  'Setup complete',
] as const;

/** The active milestone index (0–3) for a phase. Terminal phases sit at the
 *  full-history milestone — that is where the loading spine stopped. */
export function installMilestoneIndex(phase: InstallPhase | null): number {
  switch (phase) {
    case 'CONNECTING':
    case 'CONNECTED':
      return 0;
    case 'LOADING_RECENT':
    case 'RECENT_READY':
      return 1;
    case 'LOADING_FULL':
    case 'DEGRADED':
    case 'BROWSE_ONLY':
      return 2;
    case 'FULL_READY':
      return 3;
    default:
      return 0;
  }
}
